/**
 * GamesListTable - Reusable table component for displaying game lists.
 *
 * Used in BlocksPage, CourtsPage, TeamOverviewModal, and TeamPage.
 * Features:
 * - Time shown as start-end range
 * - Clickable Block, Court, and Team names
 * - Kyykkä-style score display
 * - Status chips
 *
 * Future: Add referee column when referee allocation is implemented.
 */

import type { ElementType } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
} from '@mui/material';
import ScoreIcon from '@mui/icons-material/SportsScore';
import TrophyIcon from '@mui/icons-material/EmojiEvents';

import { formatTime } from '@/lib/i18n';
import type { Game, GameStatus } from '@/types';
import { getGameStatus, generatePaperId } from '@/types';

// ============================================================================
// Types
// ============================================================================

export interface GamesListTableProps {
  /** Array of games to display */
  games: Game[];
  /** Current team ID for perspective-based display (shows "opponent" and "result" columns) */
  teamPerspective?: number;
  /** Column visibility options */
  columns?: {
    time?: boolean;
    actualTime?: boolean;  // Show actual start/end times in time column
    block?: boolean;
    court?: boolean;
    teams?: boolean;
    score?: boolean;
    status?: boolean;
  };
  /** Click handlers for interactive elements */
  onGameClick?: (gameId: number) => void;
  onBlockClick?: (paperId: number) => void;
  onCourtClick?: (courtId: number) => void;
  onTeamClick?: (teamId: number) => void;
  /** Whether to show the table in a Paper wrapper */
  variant?: 'outlined' | 'elevation';
  /** Max height for scrollable container */
  maxHeight?: number | string;
}

// ============================================================================
// Helpers
// ============================================================================

/** Format time range from start to end */
function formatTimeRange(startStr: string, endStr: string): string {
  return `${formatTime(startStr)} - ${formatTime(endStr)}`;
}

/** Convert backend score to kyykkä display score */
function toTotalDisplayScore(backendScore: number | null): number | null {
  if (backendScore === null) return null;
  return backendScore;
}

/** Status chip configuration */
const STATUS_CONFIG: Record<GameStatus, { color: 'default' | 'primary' | 'success' | 'error'; label: string }> = {
  scheduled: { color: 'default', label: 'Scheduled' },
  in_progress: { color: 'primary', label: 'In Progress' },
  finished: { color: 'success', label: 'Finished' },
  cancelled: { color: 'error', label: 'Cancelled' },
};

// ============================================================================
// Component
// ============================================================================

export function GamesListTable({
  games,
  teamPerspective,
  columns = {
    time: true,
    block: true,
    court: true,
    teams: true,
    score: true,
    status: true,
  },
  onGameClick,
  onBlockClick,
  onCourtClick,
  onTeamClick,
  variant = 'outlined',
  maxHeight,
}: GamesListTableProps) {
  // Sort games by schedule_start
  const sortedGames = [...games].sort(
    (a, b) => new Date(a.schedule_start).getTime() - new Date(b.schedule_start).getTime()
  );

  // Team perspective helpers
  const getOpponent = (game: Game): { id: number | null; name: string } => {
    if (!teamPerspective) return { id: null, name: '' };
    if (game.team1 === teamPerspective) {
      return { id: game.team2, name: game.team2_name || 'TBD' };
    }
    return { id: game.team1, name: game.team1_name || 'TBD' };
  };

  const getTeamScores = (game: Game): { teamScore: number | null; opponentScore: number | null } => {
    if (!teamPerspective) return { teamScore: null, opponentScore: null };
    if (game.team1 === teamPerspective) {
      return {
        teamScore: toTotalDisplayScore(game.team1_score),
        opponentScore: toTotalDisplayScore(game.team2_score),
      };
    }
    return {
      teamScore: toTotalDisplayScore(game.team2_score),
      opponentScore: toTotalDisplayScore(game.team1_score),
    };
  };

  const getResult = (game: Game): 'win' | 'loss' | 'tie' | null => {
    if (!teamPerspective) return null;
    const status = getGameStatus(game);
    if (status !== 'finished') return null;
    if (game.is_tie) return 'tie';
    if (game.winner_id === teamPerspective) return 'win';
    return 'loss';
  };

  // Clickable text style
  const clickableStyle = {
    cursor: 'pointer',
    color: 'primary.main',
    '&:hover': { textDecoration: 'underline' },
  };

  // Wrapper component - use type assertion for conditional component
  const Wrapper: ElementType = variant === 'outlined' ? Paper : Box;
  const wrapperProps = variant === 'outlined' ? { variant: 'outlined' as const } : {};

  if (sortedGames.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
        No games
      </Typography>
    );
  }

  return (
    <Wrapper {...wrapperProps}>
      <Box sx={{ overflowX: 'auto', maxHeight }}>
        <Box
          component="table"
          sx={{
            width: '100%',
            borderCollapse: 'collapse',
            '& th, & td': {
              px: 2,
              py: 1,
              textAlign: 'left',
              borderBottom: '1px solid',
              borderColor: 'divider',
            },
            '& th': {
              fontWeight: 600,
              bgcolor: 'action.hover',
              position: maxHeight ? 'sticky' : 'static',
              top: 0,
              zIndex: 1,
            },
          }}
        >
          <thead>
            <tr>
              {columns.time && <th>Time</th>}
              {columns.block && <th>Block</th>}
              {columns.court && <th>Court</th>}
              {/* Show "Opponent" when in team perspective mode, otherwise "Teams" */}
              {columns.teams && <th>{teamPerspective ? 'Opponent' : 'Teams'}</th>}
              {columns.score && <th style={{ textAlign: 'center' }}>Score</th>}
              {columns.status && <th>{teamPerspective ? 'Result' : 'Status'}</th>}
              {/* TODO: Add referee column when referee allocation is implemented */}
            </tr>
          </thead>
          <tbody>
            {sortedGames.map((game) => {
              const status = getGameStatus(game);
              const config = STATUS_CONFIG[status];
              const winnerId = game.winner_id;

              // Team perspective values
              const opponent = getOpponent(game);
              const teamScores = getTeamScores(game);
              const result = getResult(game);

              return (
                <tr key={game.id}>
                  {/* Time column - show range with optional actual times */}
                  {columns.time && (
                    <td>
                      <Typography
                        variant="body2"
                        sx={onGameClick ? clickableStyle : undefined}
                        onClick={() => onGameClick?.(game.id)}
                      >
                        {formatTimeRange(game.schedule_start, game.schedule_end)}
                      </Typography>
                      {columns.actualTime && game.time_start && (
                        <Typography
                          variant="caption"
                          color="info.main"
                          sx={{ display: 'block' }}
                        >
                          Started: {formatTime(game.time_start)}
                        </Typography>
                      )}
                      {columns.actualTime && game.time_end && (
                        <Typography
                          variant="caption"
                          color="success.main"
                          sx={{ display: 'block' }}
                        >
                          Ended: {formatTime(game.time_end)}
                        </Typography>
                      )}
                    </td>
                  )}

                  {/* Block column - clickable */}
                  {columns.block && (
                    <td>
                      <Typography
                        variant="body2"
                        sx={onBlockClick ? clickableStyle : undefined}
                        onClick={() => onBlockClick?.(game.paper_id)}
                      >
                        {generatePaperId(game.paper_id)}
                      </Typography>
                    </td>
                  )}

                  {/* Court column - clickable */}
                  {columns.court && (
                    <td>
                      {game.court_id ? (
                        <Typography
                          variant="body2"
                          sx={onCourtClick ? clickableStyle : undefined}
                          onClick={() => game.court_id && onCourtClick?.(game.court_id)}
                        >
                          {game.court_id}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </td>
                  )}

                  {/* Teams/Opponent column */}
                  {columns.teams && (
                    <td>
                      {teamPerspective ? (
                        // Team perspective: show opponent only
                        <Typography
                          variant="body2"
                          sx={onTeamClick && opponent.id ? clickableStyle : undefined}
                          onClick={() => opponent.id && onTeamClick?.(opponent.id)}
                        >
                          {opponent.name}
                        </Typography>
                      ) : (
                        // Normal view: show both teams
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography
                            variant="body2"
                            fontWeight={winnerId === game.team1 ? 700 : 400}
                            color={winnerId === game.team1 ? 'success.main' : 'primary.main'}
                            sx={onTeamClick && game.team1 ? clickableStyle : undefined}
                            onClick={() => game.team1 && onTeamClick?.(game.team1)}
                          >
                            {game.team1_name || 'TBD'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            vs
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight={winnerId === game.team2 ? 700 : 400}
                            color={winnerId === game.team2 ? 'success.main' : 'primary.main'}
                            sx={onTeamClick && game.team2 ? clickableStyle : undefined}
                            onClick={() => game.team2 && onTeamClick?.(game.team2)}
                          >
                            {game.team2_name || 'TBD'}
                          </Typography>
                        </Box>
                      )}
                    </td>
                  )}

                  {/* Score column */}
                  {columns.score && (
                    <td style={{ textAlign: 'center' }}>
                      {status === 'finished' || status === 'in_progress' ? (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.5,
                          }}
                        >
                          <ScoreIcon fontSize="small" color="action" />
                          <Typography variant="body2" fontFamily="monospace">
                            {teamPerspective ? (
                              // Team perspective: show team score - opponent score
                              <>
                                {teamScores.teamScore ?? '-'} - {teamScores.opponentScore ?? '-'}
                              </>
                            ) : (
                              // Normal view: show team1 - team2
                              <>
                                {toTotalDisplayScore(game.team1_score) ?? '-'} -{' '}
                                {toTotalDisplayScore(game.team2_score) ?? '-'}
                              </>
                            )}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </td>
                  )}

                  {/* Status/Result column */}
                  {columns.status && (
                    <td>
                      {teamPerspective ? (
                        // Team perspective: show result
                        <>
                          {result === 'win' && (
                            <Chip icon={<TrophyIcon />} label="Win" size="small" color="success" />
                          )}
                          {result === 'loss' && <Chip label="Loss" size="small" color="error" />}
                          {result === 'tie' && <Chip label="Tie" size="small" color="warning" />}
                          {result === null && status === 'in_progress' && (
                            <Chip label="In Progress" size="small" color="primary" />
                          )}
                          {result === null && status === 'scheduled' && (
                            <Chip label="Scheduled" size="small" color="default" />
                          )}
                          {result === null && status === 'cancelled' && (
                            <Chip label="Cancelled" size="small" color="error" variant="outlined" />
                          )}
                        </>
                      ) : (
                        // Normal view: show status
                        <Chip label={config.label} size="small" color={config.color} />
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </Box>
      </Box>
    </Wrapper>
  );
}

